import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { AuthService } from '../../../shared/services/auth.service';
import { ApiService } from '../../../services/api.service';
import { User } from '../../../shared/models/models';
import { VereinService } from '../../../services/verein.service';
import { Observable, Subscription } from 'rxjs';
import { LicenseManagementService, LicenseStatusResponse } from '../../../services/license-management.service';
import { NetworkStateService } from '../../../shared/services/network-state.service';
import { getTrialDaysLeft, isTrialActive } from '../../../shared/utils/trial.util';
import { ThemeMode } from '../../../shared/services/preferences.service';
import { ThemeService } from '../../../shared/services/theme.service';

interface Pruefung {
  kurs_id: number;
  kurs_name: string;
  schein: string;
  pruefung_datum: string;
  pruefung_ort_strasse: string | null;
  pruefung_ort_plz: string | null;
  pruefung_ort_stadt: string | null;
  tage_bis_pruefung: number;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  isMenuOpen = signal(false);
  user: User | null = null;
  private userSubscription?: Subscription;
  networkStatus$!: Observable<{ isOnline: boolean }>;
  pruefung: Pruefung | null = null;
  vereinsName: string | null = null;
  showPruefungModal = false;
  licenseStatus: LicenseStatusResponse | null = null;
  licenseStatusLoaded = false;
  private licenseStatusSubscription?: Subscription;
  currentTheme: ThemeMode = 'dark';
  private themeSubscription?: Subscription;
  readonly logoDark = 'assets/logo_invertiert_transparent.png';
  readonly logoLight = 'assets/logo_light.png';

  constructor(
    private authService: AuthService,
    private api: ApiService,
    private router: Router,
    private vereinService: VereinService,
    private licenseService: LicenseManagementService,
    private networkState: NetworkStateService,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.networkStatus$ = this.networkState.getNetworkStatusObservable();
    this.networkState.refreshNow();
    this.currentTheme = this.themeService.currentTheme;
    this.themeSubscription = this.themeService.themeChanges.subscribe((mode) => {
      this.currentTheme = mode;
    });

    // Load user info
    this.user = this.authService.getCurrentUser();
    // Falls User fehlt oder noch nicht vollständig ist, vom Server nachladen
    if (!this.user || !this.user.role) {
      this.authService.fetchUserFromServer().subscribe();
    }
    this.loadVereinsName();

    // Subscribe to user changes
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.user = user;
      this.loadVereinsName();
      // Lade Prüfung wenn User eingeloggt
      if (user) {
        this.loadNaechstePruefung();
        this.loadLicenseStatus();
      } else {
        this.pruefung = null;
        this.licenseStatus = null;
        this.licenseStatusLoaded = false;
      }
    });

    // Lade Prüfung initial wenn User vorhanden
    if (this.user) {
      this.loadNaechstePruefung();
      this.loadLicenseStatus();
    }

  }

  private loadVereinsName(): void {
    this.vereinsName = null;
    if (this.user && this.user.verein_id) {
      this.vereinService.getVereinDetails(this.user.verein_id).subscribe({
        next: (res) => {
          if (res.success && res.verein && res.verein.name) {
            this.vereinsName = res.verein.name;
          }
        },
        error: () => {
          this.vereinsName = null;
        }
      });
    }
  }

  loadNaechstePruefung(): void {
    this.api.get<{ success: boolean; pruefung: Pruefung | null }>('verein/naechste-pruefung.php')
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.pruefung = response.pruefung;
          }
        },
        error: (err) => {
          console.error('Fehler beim Laden der Prüfung:', err);
        }
      });
  }

  private loadLicenseStatus(): void {
    this.licenseStatusLoaded = false;
    this.licenseStatusSubscription?.unsubscribe();
    this.licenseStatusSubscription = this.licenseService.getLicenseStatusCached().subscribe({
      next: status => {
        this.licenseStatus = status;
        this.licenseStatusLoaded = true;
      },
      error: (err) => {
        console.error('Fehler beim Laden des Lizenzstatus:', err);
        this.licenseStatusLoaded = true;
      }
    });
  }

  openPruefungModal(): void {
    this.showPruefungModal = true;
  }

  closePruefungModal(): void {
    this.showPruefungModal = false;
  }

  getGoogleMapsUrl(): string {
    if (!this.pruefung) return '';

    const addressParts = [
      this.pruefung.pruefung_ort_strasse,
      this.pruefung.pruefung_ort_plz,
      this.pruefung.pruefung_ort_stadt
    ].filter(part => part && part.trim() !== '');

    const address = addressParts.join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }

  hasLocation(): boolean {
    if (!this.pruefung) return false;
    return !!(this.pruefung.pruefung_ort_strasse || this.pruefung.pruefung_ort_plz || this.pruefung.pruefung_ort_stadt);
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.licenseStatusSubscription?.unsubscribe();
    this.themeSubscription?.unsubscribe();
  }

  toggleMenu(): void {
    this.isMenuOpen.set(!this.isMenuOpen());
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  navigateTo(path: string): void {
    this.closeMenu();
    this.router.navigate([path]);
  }

  logout(): void {
    this.authService.logout();
    this.closeMenu();
  }

  isAdmin(): boolean {
    return this.user?.role === 'ADMIN';
  }

  isVereinAdmin(): boolean {
    return this.user?.role === 'TRAINER' || this.user?.role === 'VEREINS_ADMIN';
  }

  hasActiveLicense(): boolean {
    const active = this.licenseStatus?.active_license;
    if (!active) return false;
    const validStatus = active.status === 1;
    const notExpired = active.expires_at ? new Date(active.expires_at) >= new Date() : true;
    return validStatus && notExpired;
  }

  getTrialDaysLeft(): number {
    return getTrialDaysLeft(this.user);
  }

  shouldShowTrialCountdown(): boolean {
    if (!this.licenseStatusLoaded) return false;
    const daysLeft = this.getTrialDaysLeft();
    return isTrialActive(this.user) && daysLeft > 0 && !this.hasActiveLicense();
  }

  getTrialTooltip(): string {
    const daysLeft = this.getTrialDaysLeft();
    return `Du befindest dich im Testzeitraum. Verbleibende Tage: ${daysLeft}. Bitte aktiviere rechtzeitig eine Lizenz.`;
  }

  get logoSrc(): string {
    return this.currentTheme === 'light' ? this.logoLight : this.logoDark;
  }
}
